import {
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ContactDetailComponent } from '../../organization/contacts/contact-detail/contact-detail.component';
import { PaginationComponent } from '../../inputs/pagination/pagination.component';
import { EditContactModalComponent } from '../../forms/edit-contact-modal/edit-contact-modal.component';
import { ContactsService } from '../../../services/contacts/contacts.service';
import { IContact, IPagination } from '../../../types/contactModule';
import { PromptCardComponent } from '../../toasts-and-cards/prompt-card/prompt-card.component';
import { FeedbackCardComponent } from '../../toasts-and-cards/feedback-card/feedback-card.component';
import { HasPermissionDirective } from '../../../directives/has-permission.directive';
import { EmptyStateComponent } from '../../ui/empty-state/empty-state.component';
import { DataTableComponent } from '../../ui/data-table/data-table.component';
import { StatusComponent } from '../../ui/status/status.component';
import { PermissionsService } from '../../../services/permissions/permissions.service';
import { ContactRow, contactName, toContactRows } from './contact-row';

/**
 * The contact list's table: selection, inline row actions, the contact panel,
 * edit and delete, and server pagination. The parent (contact-list) owns the
 * search, filters and sort and passes them in as `params`.
 */
@Component({
  selector: 'contacts-table',
  standalone: true,
  imports: [
    MatTooltipModule,
    ContactDetailComponent,
    PaginationComponent,
    EditContactModalComponent,
    PromptCardComponent,
    FeedbackCardComponent,
    HasPermissionDirective,
    EmptyStateComponent,
    DataTableComponent,
    StatusComponent,
  ],
  templateUrl: './contact-table.component.html',
  styleUrl: './contact-table.component.css',
})
export class ContactTableComponent implements OnInit, OnChanges, OnDestroy {
  contactService = inject(ContactsService);
  private permissions = inject(PermissionsService);
  private destroyRef = inject(DestroyRef);

  @Output() updateSelectedContacts = new EventEmitter<IContact[]>();
  /** Search, filters and sort from the parent. Page and size are added here. */
  @Input() params!: Record<string, any>;
  /** The active search, for the empty state. */
  @Input() searchTerm: string = '';
  /** True when filters narrow the list, for the empty state. */
  @Input() filtered: boolean = false;
  /** Asks the parent to clear the search and filters. */
  @Output() clearFilters = new EventEmitter<void>();

  showDeletePrompt: boolean = false;

  contacts: IContact[] = [];
  rows: ContactRow[] = [];
  contactsParam = {};
  selectedContact: IContact | null = null;
  showContactDetail: boolean = false;
  // Edit Contact stuff
  openEditContactModal: boolean = false;
  contactsPage: number = 0;
  totalContactsPages: number = 1;
  totalContacts: number = 0;
  pageSize: number = 10;
  errorMsg: string = '';
  successMsg: string = '';
  timer: ReturnType<typeof setTimeout> | null = null;

  /** Rows open the contact panel only for people allowed to see it. */
  canViewDetails = false;

  // contacts manipulation controls ==> results are emitted to contact list for bulk actions
  selectedContacts: IContact[] = [];
  allContactsSelected: boolean = false;

  get selectedName(): string {
    return contactName(this.selectedContact) || 'this contact';
  }

  get someOnPageSelected(): boolean {
    return !this.allContactsSelected && this.contacts.some((contact) => this.checkedMember(contact));
  }

  // contact selection Controls
  selectAll() {
    const merged = new Map<string, IContact>();
    this.selectedContacts.forEach((el) => merged.set(this.contactKey(el), el));
    this.contacts.forEach((el) => merged.set(this.contactKey(el), el));
    this.selectedContacts = Array.from(merged.values());
  }
  unselectAll() {
    const onPage = new Set(this.contacts.map((el) => this.contactKey(el)));
    this.selectedContacts = this.selectedContacts.filter((el) => !onPage.has(this.contactKey(el)));
  }

  toggleCheckAll() {
    if (this.contacts.length && this.contacts.every((element) => this.checkedMember(element))) {
      this.unselectAll();
      this.allContactsSelected = false;
    } else {
      this.selectAll();
      this.allContactsSelected = true;
    }
    this.emitSelection();
  }

  /** Stable row identity for selection (DB id is often 0 / duplicated in API payloads). */
  contactKey(contact: IContact): string {
    if (contact.uuid != null && String(contact.uuid).length > 0) {
      return String(contact.uuid);
    }
    if (contact.id != null && `${contact.id}`.length > 0) {
      return String(contact.id);
    }
    return '';
  }

  trackByContact(index: number, contact: IContact): string | number {
    const k = this.contactKey(contact);
    return k || index;
  }

  checkedMember(contact: IContact) {
    const key = this.contactKey(contact);
    if (!key) return false;
    return this.selectedContacts.some((el) => this.contactKey(el) === key);
  }

  resetSelectedContacts() {
    this.selectedContacts = [];
    this.allContactsSelected = false;
  }

  emitSelection() {
    this.updateSelectedContacts.emit(this.selectedContacts);
  }

  addToSelection(contact: IContact) {
    this.selectedContacts = [...this.selectedContacts, contact];
    this.emitSelection();
  }

  removeFromSelection(contact: IContact) {
    const key = this.contactKey(contact);
    this.selectedContacts = this.selectedContacts.filter((el) => this.contactKey(el) !== key);
    this.allContactsSelected = false;
    this.emitSelection();
  }

  select(contact: IContact) {
    if (this.checkedMember(contact)) {
      this.removeFromSelection(contact);
    } else {
      this.addToSelection(contact);
      this.syncAllSelected();
    }
    this.emitSelection();
  }

  private syncAllSelected() {
    this.allContactsSelected =
      this.contacts.length > 0 && this.contacts.every((contact) => this.checkedMember(contact));
  }

  /** Call when parent changes sort/filter so pagination matches `params.page` before the next response. */
  resetToPageZero(): void {
    this.contactsPage = 0;
  }

  openContactDetail(contact: IContact) {
    this.updateSelectedContact(contact);
    this.showContactDetail = true;
  }
  closeContactDetail() {
    this.showContactDetail = false;
  }

  /** Row click: open the contact panel when allowed. */
  openRow(row: ContactRow) {
    if (!this.canViewDetails) return;
    this.openContactDetail(row.contact);
  }

  /** Enter on the row opens it; Enter on a row button or checkbox stays with that control. */
  openFromKeyboard(event: Event, row: ContactRow) {
    if (event.target !== event.currentTarget) return;
    event.preventDefault();
    this.openRow(row);
  }

  openEditContact(contact: IContact) {
    this.updateSelectedContact(contact);
    this.openEditContactModal = true;
  }
  closeEditContact() {
    this.openEditContactModal = false;
  }

  openDeletePrompt(contact: IContact) {
    this.updateSelectedContact(contact);
    this.showDeletePrompt = true;
  }
  closeDeletePrompt() {
    this.showDeletePrompt = false;
  }

  updateSelectedContact(contact: IContact) {
    this.selectedContact = contact;
  }

  private flash(kind: 'success' | 'error', message: string) {
    if (this.timer) clearTimeout(this.timer);
    this.successMsg = kind === 'success' ? message : '';
    this.errorMsg = kind === 'error' ? message : '';
    this.timer = setTimeout(() => {
      this.successMsg = '';
      this.errorMsg = '';
    }, 5000);
  }

  async deleteContact() {
    const name = this.selectedName;
    const deleted = this.selectedContact;
    this.contactService.deleteContact(this.selectedContact?.uuid as string).subscribe({
      next: () => {
        this.closeDeletePrompt();
        if (deleted) this.removeFromSelection(deleted);
        this.flash('success', `Deleted ${name}.`);
      },
      error: () => {
        this.flash('error', `${name} was not deleted. Try again.`);
      },
      complete: () => {},
    });
  }

  get updatedParams() {
    // Spring Data Pageable expects `page` and `size` (not `pageSize`).
    const base = { ...((this.params ?? this.contactsParam) as Record<string, any>) };
    delete base['pageSize'];
    return { ...base, page: this.contactsPage, size: this.pageSize };
  }

  updateContactsPage(newPage: number) {
    this.contactsPage = newPage;
    this.contactService.fetchContacts(this.updatedParams);
  }
  goToNextPage() {
    this.contactsPage += 1;
    this.contactService.fetchContacts(this.updatedParams);
  }
  goToPreviousPage() {
    this.contactsPage -= 1;
    this.contactService.fetchContacts(this.updatedParams);
  }

  /** The pager follows a size change with changePage(0), which fetches. */
  onPageSizeChange(size: number) {
    this.pageSize = size;
    this.contactsPage = 0;
  }

  get contactLoading() {
    return this.contactService.isLoading;
  }

  get loading() {
    return this.contactService.isFetching;
  }

  ngOnInit() {
    this.contactService.getContacts
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((contacts: IContact[]) => {
        this.contacts = contacts ?? [];
        this.rows = toContactRows(this.contacts);
        this.syncAllSelected();
      });

    this.contactService.getContactsPaginationData
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data: IPagination) => {
        if (!data) return;
        this.contactsPage = data.pageNumber ?? 0;
        this.totalContactsPages = Math.max(1, data.totalPages ?? 1);
        this.totalContacts = data.totalElements ?? 0;
        if (data.pageSize) this.pageSize = data.pageSize;
      });

    this.permissions
      .hasPermission('CONTACT_MANAGEMENT', 'view-contact-history')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((allowed) => (this.canViewDetails = !!allowed));
  }

  ngOnChanges() {
    this.contactsParam = this.params;
    if (this.params?.['size'] != null) {
      this.pageSize = Number(this.params['size']);
    }
    if (this.params?.['page'] != null) {
      this.contactsPage = Number(this.params['page']);
    }
  }

  ngOnDestroy() {
    this.timer && clearTimeout(this.timer!);
  }
}
